import FingerprintJS from '@fingerprintjs/fingerprintjs'
import {Platform} from 'react-native'
import DeviceInfo from 'react-native-device-info'
import {UserCredentials} from 'react-native-keychain'

import APIClient, {APIClientOptions, ClientInfo} from '@possible/generated/APIClient'
import {Consumer, EnvironmentType} from '@possible/cassandra'
import {apiUriDev, apiUriProd} from '@possible/generated/src/api/lib/ApiUris'

import {STORE_USER_ID_TOKEN_ACTION, USER_LOGGED_IN_ACTION} from 'src/api/actions/actionsNames'
import {ConnectionError, ConnectionSuccess} from 'src/api/actions/client/connectionActions'
import {APIStateChange} from 'src/api/actions/index'
import APIClientLoan from 'src/api/lib/APIClientLoan'
import {Logout} from 'src/api/MobileGatewayAPI/actions/logout'
import {ApiReduxState, UserLoginStates} from 'src/api/reducers/types'
import {initializeCassandra} from 'src/cassandra/initializeCassandra'
import {API_CLIENT_LOGGING} from 'src/config'
import {SetAmplitudeUserProperties, SetUserId} from 'src/lib/Analytics/analytics_compat'
import {setShouldEnrollInCards} from 'src/lib/card/actions'
import {readDevMode, readEnv} from 'src/lib/devMode'
import {CompoundVersion} from 'src/lib/expo/versioning'
import Firebase from 'src/lib/firebase'
import {isJailBroken} from 'src/lib/jailBreak'
import * as Keychain from 'src/lib/keychain'
import Log from 'src/lib/loggingUtil'
import {clearDeeplink, getDeeplink} from 'src/lib/singular/utils'
import {getEnvironment} from 'src/lib/utils/environmentUtil'
import {isDeviceWeb} from 'src/lib/utils/platform'
import {PfReduxState} from 'src/reducers/types'
import {PfDispatch, PfGetState} from 'src/store/types'

const AppUIVersionPropertyName = 'app_ui_version'

//// THUNKS

const trackAppUiVersion = (): void => {
  SetAmplitudeUserProperties(
    `[Possible Finance] ${AppUIVersionPropertyName}`,
    CompoundVersion() ?? 'N/A',
  )
}

const trackDeviceJailbroken = (userId?: string): void => {
  if (isJailBroken()) {
    Log.warn(`Device is jailbroken! userId: ${userId}`)
  }
}

export function UserLoggedIn(
  _refreshLoginMethod?: boolean,
  isLoginFromRegistration: boolean = false,
) {
  return async (dispatch: PfDispatch, getState: PfGetState): Promise<boolean> => {
    const userId = getState().api.user_id
    const token = getState().api.user_token

    if (!userId || !token) {
      return false
    }

    SetUserId(userId)
    trackAppUiVersion()

    await initializeCassandra(dispatch, token)

    dispatch(APIStateChange({initialUserRefresh: true}))
    if (!isLoginFromRegistration) {
      dispatch(UserLoggedInAction())
    }
    return true
  }
}
export function GetURIsFromEnv(env: EnvironmentType): typeof apiUriDev | typeof apiUriProd {
  switch (env) {
    case EnvironmentType.LocalJava:
    case EnvironmentType.Local:
    case EnvironmentType.Dev:
      return apiUriDev
    case EnvironmentType.NotSet:
    case EnvironmentType.Prod:
    default:
      //default to production
      return apiUriProd
  }
}

export async function GetURIs(): Promise<ReturnType<typeof GetURIsFromEnv>> {
  const env = await getEnvironment()
  return GetURIsFromEnv(env)
}

export async function GetClientInfo(): Promise<ClientInfo> {
  const info: ClientInfo = {}
  try {
    if (isDeviceWeb()) {
      // we have only a few available properties for web browsers
      info.clientType = 'web_app'
      info.deviceId = (await (await FingerprintJS.load()).get()).visitorId
      info.deviceOsName = await DeviceInfo.getBaseOs()
    } else {
      info.clientType = 'mobile_app'
      info.deviceId = DeviceInfo.getUniqueIdSync() // BE uses appUniqueId for device id
      info.deviceCarrier = (await DeviceInfo.getCarrier()).substring(0, 40)
      info.deviceMfg = (await DeviceInfo.getManufacturer()).substring(0, 40)
      info.deviceOsName = Platform.OS
    }

    info.appUniqueId = info.deviceId
  } catch (error: unknown) {
    let message = 'Unknown Error'
    if (error instanceof Error) {
      message = error.message
    }
    Log.log(`getClientInfo failed, e : ${message}`)
  }
  return info
}

async function getAPIClientOptions(
  dispatch: PfDispatch,
  getState: () => PfReduxState,
  userCreds: UserCredentials | undefined,
): Promise<APIClientOptions> {
  try {
    const uris = await GetURIs()

    const handleLoggedIn = async (): Promise<boolean> => {
      const isUserLoggedIn = await dispatch(UserLoggedIn(true))

      if (isUserLoggedIn) {
        dispatch(
          APIStateChange({
            user_logged_state: UserLoginStates.logged_in,
          }),
        )
      }
      return isUserLoggedIn
    }

    const handleNotLoggedIn = (): void => {
      dispatch(
        APIStateChange({
          user_logged_state: UserLoginStates.not_logged_in,
          user_id: undefined,
          user_token: undefined,
        }),
      )
    }

    const handleLogout = (): void => {
      dispatch(Logout(true)).catch((e) => {
        // making this an error as at least initially we may want
        // some insight into how frequently this is happening
        Log.error(e, 'Error while logging out: ')
      })
    }

    const handleConnectionError = (): void => {
      dispatch(ConnectionError())
    }

    const handleConnectionSuccess = (): void => {
      dispatch(ConnectionSuccess())
    }

    const userId = getState().api.user_id
    const clientInfo = await GetClientInfo()

    const instanceId = Firebase.instanceId
    dispatch(APIStateChange({instanceId}))

    const handleStateChange = (newState: Partial<ApiReduxState>): void => {
      dispatch(APIStateChange(newState))
    }

    const isInDevMode = await readDevMode()

    return {
      shouldLog: API_CLIENT_LOGGING,

      log: Log.log,
      warn: Log.warn,
      error: Log.error,

      username: userCreds && userCreds?.username !== '_pfo' ? userCreds.username : undefined,
      password: userCreds && userCreds?.password !== '1' ? userCreds.password : undefined,
      // this is silly but TS needs a string here. we're deleting this soon anyways so whatever
      userId: userId ?? '',
      userToken: userId ?? '',

      devMode: isInDevMode,

      instanceId,

      ...uris,

      badConnection: getState()?.api.badConnection,
      maintainenceOn: false,

      onLoggedIn: handleLoggedIn,
      onNotLoggedIn: handleNotLoggedIn,
      onLogout: handleLogout,

      onConnectionError: handleConnectionError,
      onConnectionSuccess: handleConnectionSuccess,
      checkIsMaintenance: () => Promise.resolve(false),
      onStateChange: handleStateChange,

      platform: Platform.OS,

      clientInfo,
    }
  } catch (e) {
    Log.error(e, 'getAPIClientOptions: ')
    throw e
  }
}

export function InitClient() {
  return async (dispatch: PfDispatch, getState: PfGetState): Promise<void> => {
    try {
      const userCreds = await readUserId()
      const userId = userCreds ? userCreds.username : undefined
      await Firebase.init()

      const apiClientOptions = await getAPIClientOptions(dispatch, getState, userCreds)
      const envOverride = await readEnv()
      await initializeCassandra(dispatch, userCreds?.password)

      APIClientLoan.init(dispatch, getState)
      // APIClient.init depends on PfOpenApi being initialized
      const didSuccessfullyInit = await APIClient.init(apiClientOptions)
      if (didSuccessfullyInit === false) {
        throw Error('DoNotLog')
      }

      if (userId && userCreds && userCreds.password !== '1') {
        void (await Firebase.registerNotifications())
      }
      dispatch(
        APIStateChange({
          init: true,
          options: apiClientOptions,
          envOverride,
        }),
      )

      trackDeviceJailbroken(userId)
    } catch (e) {
      if (e instanceof Error && e.message !== 'DoNotLog') {
        Log.error(e, 'Unable to complete InitClient')
      }
      await dispatch(Logout())
      dispatch(APIStateChange({init: true}))
    }
  }
}

function StoreUserIdTokenAction(userId: string, token: string) {
  return (dispatch: PfDispatch): void => {
    dispatch({type: STORE_USER_ID_TOKEN_ACTION, user_id: userId, token})
  }
}

function UserLoggedInAction(): {type: typeof USER_LOGGED_IN_ACTION} {
  return {type: USER_LOGGED_IN_ACTION}
}

const handlePostLoginDeeplinks = async (userId: string, dispatch: PfDispatch): Promise<void> => {
  const deeplink = getDeeplink('offer')
  if (deeplink) {
    const {params} = deeplink

    try {
      await Consumer.methods.partnerLinkUserWithOffer(params.offer_id)
      await clearDeeplink(deeplink)
    } catch (e) {
      Log.error(e, `Error linking partner offer. offer_id: "${params.offer_id}"`)
    }
  }

  const cardDeeplink = getDeeplink('card_landing')
  if (cardDeeplink) {
    try {
      dispatch(setShouldEnrollInCards())
      await clearDeeplink(cardDeeplink)
    } catch (e) {
      Log.error(e, 'Error adding user to card group.')
    }
  }
}

export async function initUserAuth({
  userId,
  accessToken,
  dispatch,
  isLoginFromRegistration = false,
}: {
  userId: string
  accessToken: string
  dispatch: PfDispatch
  isLoginFromRegistration: boolean
}): Promise<void> {
  await saveUserId(userId, accessToken)

  APIClient.updateOptions({
    userId,
    userToken: accessToken,
  })
  await Firebase.init()

  await handlePostLoginDeeplinks(userId, dispatch)

  dispatch(StoreUserIdTokenAction(userId, accessToken))
  await Firebase.registerNotifications()

  await dispatch(UserLoggedIn(false, isLoginFromRegistration))
}

async function saveUserId(username: string, password: string): Promise<void> {
  try {
    return await Keychain.setServicePassword(username, password)
  } catch (e) {
    Log.error(e, 'Error saving credentials')
    return
  }
}

async function readUserId(): Promise<UserCredentials | undefined> {
  //This method supports transitioning from storing our service password in the generic key store, to the specific service key store
  //The Firebase SDK was overwriting the generic key store. https://github.com/oblador/react-native-keychain/issues/363
  try {
    const servicePassword = await Keychain.getServicePassword()

    if (servicePassword === false) {
      //check generic key store and attempt a transition
      const userCreds = await Keychain.getGenericPassword()
      if (userCreds !== false && userCreds?.username === '_pfo') {
        //Firebase is using the apps keychain on iOS, this is conflicting with our usage of it.
        Log.debug(`firebase has overriden generic keychain`)
        return undefined
      }

      //update access token
      if (userCreds !== false) {
        await Keychain.setServicePassword(userCreds.username, userCreds.password)
        const result = await Keychain.getServicePassword()
        if (result === false) {
          return undefined
        }
        return result
      }
    } else {
      return servicePassword
    }
  } catch (e) {
    Log.info('Error reading credentials', e)
    return undefined
  }
}
